/**
 * Loading affordances.
 *
 * The product had none. Screens rendered the literal string "Loading…", and
 * tables rendered nothing at all — the empty state is suppressed while loading
 * and no placeholder took its place, so a slow fetch was indistinguishable from
 * a table with no rows. That is the worst possible ambiguity on the message
 * log, where "no rows" is a real and alarming answer.
 */

/**
 * Placeholder rows for a table that has not answered yet.
 *
 * Rendered inside `.oc-table__body`, so each row inherits the same `--oc-cols`
 * grid the real rows use and the placeholder occupies the columns the data
 * will. Content does not jump sideways when it arrives.
 *
 * `columns` should match the screen's column count. Getting it wrong is
 * cosmetic — the grid still governs — but a short row looks like a bug.
 */
export function SkeletonRows({ rows = 6, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div aria-hidden="true">
      {Array.from({ length: rows }, (_, row) => (
        <div className="oc-table__row" key={row} style={{ cursor: "default" }}>
          {Array.from({ length: columns }, (_, column) => (
            <span key={column} style={{ minWidth: 0 }}>
              {/* Varying the width stops the block reading as a rendered table
                  of identical values, which is its own kind of confusing. */}
              <span
                className={`oc-skeleton oc-skeleton__line${column === 0 ? "" : " oc-skeleton__line--short"}`}
                style={{ display: "block", width: column === 0 ? "70%" : undefined }}
              />
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Inline spinner. Inherits colour from its parent, so it works on a button, in
 * a panel, or beside text without being told which.
 */
export function Spinner({ large = false, label }: { large?: boolean; label?: string }) {
  return (
    <span
      className={`oc-spinner${large ? " oc-spinner--lg" : ""}`}
      role={label ? "status" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    />
  );
}

/** Three dots, for waits too short to deserve a spinner's authority. */
export function Dots() {
  return (
    <span className="oc-dots" aria-hidden="true">
      <span />
      <span />
      <span />
    </span>
  );
}

/**
 * A progress bar. Omit `value` for work with no total to divide by — the CSV
 * import knows how many rows it has created and never how many it will.
 */
export function Progress({ value, label }: { value?: number; label?: string }) {
  // Clamped once, for the width and the announced value alike. It used to clamp
  // only the width, which went unnoticed while nothing called this: the first
  // real caller, send usage, routinely exceeds its total — transactional mail is
  // never refused by the limit, so 49 of 40 is an ordinary state — and an
  // aria-valuenow of 123 against a maximum of 100 is announced as nonsense.
  const pct = typeof value === "number" ? Math.min(100, Math.max(0, value)) : undefined;
  return (
    <div
      className={`oc-progress${pct === undefined ? " oc-progress--indeterminate" : ""}`}
      role="progressbar"
      aria-label={label}
      aria-valuenow={pct === undefined ? undefined : Math.round(pct)}
      aria-valuemin={pct === undefined ? undefined : 0}
      aria-valuemax={pct === undefined ? undefined : 100}
    >
      <div className="oc-progress__bar" style={pct === undefined ? undefined : { width: `${pct}%` }} />
    </div>
  );
}
